import GUI from 'lil-gui';
import type * as THREE from 'three';
import type { ContactShadowConfig } from '../render/ContactShadow';
import {
  cameraBooleanKeys,
  cameraNumericKeys,
  type CameraTuning,
  type CameraBooleanKey,
  type CameraNumericKey,
} from '../game/CameraConfig';
import {
  cloneStyleConfig,
  dispatchStyleConfigChange,
  resetStyleConfig,
  saveStyleConfig,
  savePresetOverlay,
  saveStyleConfigToSource,
  saveStyleToPreset,
  type StyleConfig,
} from '../render/StyleConfig';
import { isSaveToSourceAvailable } from '../dev/saveDefaults';
import {
  readRendererBackendPreference,
  setRendererBackendPreference,
  type RendererBackendPreference,
} from '../render/Renderer';
import {
  clearOverride,
  getPath,
  isOverridden,
  MASTER_SUN_DIRECTION_PATHS,
  resolveStyle,
  setOverride,
  type OverridePath,
} from '../render/OverrideResolver';
import {
  anglesToDirection,
  applyBinarySunLayout,
  applyLightingPreset,
  directionToAngles,
  type LightingPresetName,
} from './LightingPresets';
import type { DebugLayers } from './DebugLayers';
import { createIconRail, type IconRail } from '../ui/IconRail';

export interface ShadowsTabTargets {
  sun: THREE.DirectionalLight;
  shadowConfig: {
    enabled: boolean;
    frustumSize: number;
    near: number;
    far: number;
    mapSize: number;
    softRadius: number;
    forwardBias: number;
  };
  applyShadowConfig: () => void;
  contactShadow: { config: ContactShadowConfig };
}

export type MapRacerMode = 'hidden' | 'static' | 'drive';

export interface MapRacerModeControl {
  state: { mode: MapRacerMode };
  hasRacer: boolean;
  onChange: (mode: MapRacerMode) => void;
}

export type MapViewCameraMode = 'ground' | 'fly' | 'top';

export interface MapViewCameraState {
  mode: MapViewCameraMode;
  groundDistance: number;
  groundHeight: number;
  topHeight: number;
}

export interface MapViewCameraControl {
  state: MapViewCameraState;
  onModeChange: (mode: MapViewCameraMode) => void;
  onChange: (key: keyof Omit<MapViewCameraState, 'mode'>, value: number) => void;
  onReset: () => MapViewCameraState;
}

export interface DriveCameraControl {
  tuning: CameraTuning;
  onChange: (key: CameraNumericKey, value: number) => void;
  onBooleanChange?: (key: CameraBooleanKey, value: boolean) => void;
  onSaveToCode: () => Promise<{ ok: boolean; bytes?: number; error?: string }>;
  onReset: () => CameraTuning;
}

export interface StylePanelOptions {
  config: StyleConfig;
  preset?: {
    name: string;
    label: string;
    isFallback: boolean;
  };
  /** When provided, a "Shadows" tab is added with live shadow + contact decal controls. */
  shadows?: ShadowsTabTargets;
  /** When provided, an "Isolate" tab is added that lets the user toggle individual
   *  lighting / render contributors off so the scene can be built up from black.
   *  These toggles are runtime-only and are NEVER written to StyleConfig. */
  debugLayers?: DebugLayers;
  /** Runtime-only MAP racer visibility/drive mode control. Not persisted. */
  mapRacerMode?: MapRacerModeControl;
  /** Runtime-only MAP review camera controls. Not persisted to StyleConfig. */
  mapViewCamera?: MapViewCameraControl;
  /** Gameplay camera tuning, persisted separately to Camera.defaults.json. */
  driveCamera?: DriveCameraControl;
  onExport?: (json: string) => void;
  onReset?: (config: StyleConfig) => void;
  onChange?: (config: StyleConfig) => void;
  /** Called whenever a save-to-source attempt completes (success or failure). */
  onSaveStatus?: (message: string, ok: boolean) => void;
}

const FOLDER_STATE_KEY = 'tst:style-panel:folders';
const ACTIVE_TAB_KEY = 'tst:style-panel:active-tab';
const TAB_BAR_WIDTH = 52;

type TabKey = 'actions' | 'camera' | 'lighting' | 'sky' | 'sfx' | 'shadows' | 'track' | 'ground' | 'day';

interface TabSpec {
  key: TabKey;
  label: string;
  icon: string;
  build(this: StylePanel, gui: GUI): void;
}

export class StylePanel {
  private readonly directionSyncers: Array<() => void> = [];
  private readonly overrideToggleProxies = new Map<OverridePath, { enabled: boolean }>();
  private readonly guis = new Map<TabKey, GUI>();
  private tabRail: IconRail<TabKey> | null = null;
  private activeTab: TabKey;
  readonly root: HTMLElement;

  /** Test-only: lets the slider-sweep harness enumerate every controller
   *  across every tab. See `src/debug/SliderInventory.ts`. */
  getGuis(): GUI[] {
    return Array.from(this.guis.values());
  }

  constructor(private readonly opts: StylePanelOptions) {
    injectStyles();

    this.root = document.createElement('div');
    this.root.className = 'style-panel style-panel-tabbed';

    const body = document.createElement('div');
    body.className = 'style-panel-body';

    const savebar = this.buildSaveBar();

    // Tabs are deliberately consolidated: "Lighting" owns every contributor
    // that affects how the scene is lit (atmosphere, sun rig, IBL, fog, tone
    // map, debug-isolate toggles). The old MASTER / SUN / INDIRECT / ATMOS
    // tabs were folded into Lighting so a tuning pass lives in one place.
    // Sky tab keeps sky-shader controls (gradient stops, sun discs/halos,
    // binary layout) and clouds — those paint the background, not the light.
    const tabs: TabSpec[] = [
      { key: 'actions', label: 'Actions', icon: 'AC', build: this.addActionsTab },
      ...(opts.mapViewCamera || opts.driveCamera
        ? [{ key: 'camera' as const, label: 'Camera', icon: 'CA', build: this.addCameraTab }]
        : []),
      { key: 'lighting', label: 'Lighting', icon: 'LI', build: this.addLightingTab },
      { key: 'sky', label: 'Sky', icon: 'SK', build: this.addSkyTab },
      { key: 'sfx', label: 'SFX', icon: 'FX', build: this.addSfxTab },
      ...(opts.shadows ? [{ key: 'shadows' as const, label: 'Shadows', icon: 'SH', build: this.addShadowsTab }] : []),
      { key: 'track', label: 'Track', icon: 'TR', build: this.addTrackTab },
      { key: 'ground', label: 'Ground', icon: 'GR', build: this.addGroundTab },
      { key: 'day', label: 'Day', icon: 'DY', build: this.addDayCycleTab },
    ];

    this.activeTab = readActiveTab(tabs[0].key);
    this.tabRail = createIconRail<TabKey>({
      ariaLabel: 'Style Lab sections',
      className: 'style-panel-tabs',
      itemClassName: 'style-panel-tab',
      items: tabs,
      activeKey: this.activeTab,
      buttonRole: 'tab',
      dataAttribute: 'tab',
      onSelect: (key) => this.setActiveTab(key),
    });
    this.root.appendChild(this.tabRail.root);
    this.root.appendChild(savebar);
    this.root.appendChild(body);

    for (const spec of tabs) {
      const gui = new GUI({ title: spec.label, autoPlace: false, container: body });
      const dom = gui.domElement;
      dom.classList.add('style-panel-tab-content');
      dom.dataset.tab = spec.key;
      dom.style.position = 'static';
      dom.style.width = '100%';
      this.guis.set(spec.key, gui);
      spec.build.call(this, gui);

    }

    this.applyActiveTab();
  }

  destroy(): void {
    for (const gui of this.guis.values()) gui.destroy();
    this.guis.clear();
    this.tabRail = null;
    this.root.remove();
  }

  // ───────────────────────────────────────────────── tab bodies ──────

  private addActionsTab = (gui: GUI): void => {
    const actions = {
      reset: () => {
        const next = resetStyleConfig();
        Object.assign(this.opts.config, cloneStyleConfig(next));
        this.opts.onReset?.(next);
        this.opts.onChange?.(next);
        dispatchStyleConfigChange(next);
        this.syncDirectionControls();
        this.refreshControllers();
      },
      saveToCode: () => {
        void this.persistToSource();
      },
      exportJson: () => {
        this.opts.onExport?.(JSON.stringify(this.opts.config, null, 2));
      },
      desertHero: () => this.applyPreset('desertHero'),
      hotDesertNoon: () => this.applyPreset('hotDesertNoon'),
      electricSunset: () => this.applyPreset('electricSunset'),
      purpleDusk: () => this.applyPreset('purpleDusk'),
      blueMoonNight: () => this.applyPreset('blueMoonNight'),
      neonStorm: () => this.applyPreset('neonStorm'),
    };
    const file = this.addFolder(gui, 'File', true);
    file.add(actions, 'reset').name('Reset defaults');
    const saveCtrl = file.add(actions, 'saveToCode').name('Save to code');
    if (!isSaveToSourceAvailable()) saveCtrl.disable();
    file.add(actions, 'exportJson').name('Export JSON');

    this.addMapRacerModeFolder(gui);

    const presets = this.addFolder(gui, 'Presets', true);
    presets.add(actions, 'desertHero').name('Desert Hero');
    presets.add(actions, 'hotDesertNoon').name('Hot Desert Noon');
    presets.add(actions, 'electricSunset').name('Electric Sunset');
    presets.add(actions, 'purpleDusk').name('Purple Dusk');
    presets.add(actions, 'blueMoonNight').name('Blue Moon Night');
    presets.add(actions, 'neonStorm').name('Neon Storm');
  };

  private addMapRacerModeFolder(gui: GUI): void {
    const control = this.opts.mapRacerMode;
    if (!control) return;

    const folder = this.addFolder(gui, 'Racer', true);
    const mode = folder
      .add(control.state, 'mode', {
        Hidden: 'hidden',
        Static: 'static',
        Drive: 'drive',
      })
      .name('racer mode')
      .onChange((value: MapRacerMode) => {
        control.onChange(value);
      });
    if (!control.hasRacer) mode.disable();
  }

  private addMapViewCameraFolder(gui: GUI): void {
    const control = this.opts.mapViewCamera;
    if (!control) return;

    const folder = this.addFolder(gui, 'Review Camera', true);
    folder
      .add(control.state, 'mode', {
        Ground: 'ground',
        Fly: 'fly',
        Top: 'top',
      })
      .name('view mode')
      .listen()
      .onChange((mode: MapViewCameraMode) => {
        control.onModeChange(mode);
      });
    folder
      .add(control.state, 'groundDistance', 5, 60, 0.5)
      .name('ground distance')
      .listen()
      .onChange((value: number) => {
        control.onChange('groundDistance', value);
      });
    folder
      .add(control.state, 'groundHeight', 2.6, 28, 0.1)
      .name('ground height')
      .listen()
      .onChange((value: number) => {
        control.onChange('groundHeight', value);
      });
    folder
      .add(control.state, 'topHeight', 80, 1800, 10)
      .name('top height')
      .listen()
      .onChange((value: number) => {
        control.onChange('topHeight', value);
      });
    folder.add({
      reset: () => {
        Object.assign(control.state, control.onReset());
        this.refreshControllers();
      },
    }, 'reset').name('Reset review camera');
  }

  private addDriveCameraFolder(gui: GUI): void {
    const control = this.opts.driveCamera;
    if (!control) return;

    const file = this.addFolder(gui, 'Drive Camera Template', true);
    const actions = {
      saveToCode: async () => {
        const result = await control.onSaveToCode();
        if (result.ok) {
          this.opts.onSaveStatus?.(`Saved to Camera.defaults.json (${result.bytes ?? '?'}b)`, true);
        } else {
          this.opts.onSaveStatus?.(`Camera save failed: ${result.error ?? 'unknown error'}`, false);
        }
      },
      reset: () => {
        Object.assign(control.tuning, control.onReset());
        this.refreshControllers();
      },
    };
    const saveCtrl = file.add(actions, 'saveToCode').name('Save camera to code');
    saveCtrl.domElement.dataset.action = 'save-camera';
    saveCtrl.domElement.querySelector('button')?.setAttribute('data-action', 'save-camera');
    if (!isSaveToSourceAvailable()) saveCtrl.disable();
    file.add(actions, 'reset').name('Reset camera defaults');

    const chase = this.addFolder(gui, 'Drive Camera — Chase', true);
    this.addCameraNumber(chase, control, 'chaseDistance', 'chase distance', 4, 45, 0.5);
    this.addCameraNumber(chase, control, 'chaseHeight', 'chase height', 1.5, 18, 0.25);
    this.addCameraNumber(chase, control, 'chaseLookAhead', 'chase lookahead', 2, 60, 0.5);
    this.addCameraNumber(chase, control, 'chaseTargetHeight', 'chase target h', 0, 5, 0.1);
    this.addCameraNumber(chase, control, 'chaseFovDeg', 'chase fov (idle)', 35, 90, 1);
    this.addCameraNumber(chase, control, 'chaseFovBoostDeg', 'chase fov (boost)', 50, 110, 1);
    this.addCameraNumber(chase, control, 'chaseFovFullSpeed', 'fov full speed', 20, 120, 1);
    this.addCameraNumber(chase, control, 'chaseFovResponsiveness', 'fov halflife', 0.05, 1.50, 0.05);
    this.addCameraNumber(chase, control, 'chaseHeadingVelocityBlend', 'velocity yaw blend', 0, 1, 0.05);
    this.addCameraNumber(chase, control, 'chaseHeadingDeadzoneYawRate', 'yaw deadzone', 0, 0.5, 0.01);
    this.addCameraNumber(chase, control, 'chaseHeadingDeadzoneLateralSpeed', 'lateral deadzone', 0, 6, 0.1);
    this.addCameraNumber(chase, control, 'chaseMaxYawLagDeg', 'max yaw lag', 0, 90, 1);
    this.addCameraNumber(chase, control, 'chaseTurnHeightBoost', 'turn height boost', 0, 8, 0.25);
    this.addCameraNumber(chase, control, 'chaseTurnHeightFullLateral', 'turn height full', 4, 40, 1);
    this.addCameraNumber(chase, control, 'highSpeedShakeAmp', 'hi-speed wobble', 0, 0.6, 0.01);
    this.addCameraNumber(chase, control, 'chaseVelocityCompensation', 'chase speed comp', 0, 0.25, 0.01);
    this.addCameraNumber(chase, control, 'accelLagMaxDistance', 'accel pullback', 0, 8, 0.1);
    this.addCameraNumber(chase, control, 'accelLagResponsiveness', 'accel pull in', 1, 20, 0.5);
    this.addCameraNumber(chase, control, 'accelLagReturnResponsiveness', 'accel return', 1, 12, 0.25);
    this.addCameraNumber(chase, control, 'chasePositionResponsiveness', 'chase pos halflife', 0.02, 0.50, 0.01);
    this.addCameraNumber(chase, control, 'chaseLookResponsiveness', 'chase yaw halflife', 0.02, 0.80, 0.01);

    const top = this.addFolder(gui, 'Drive Camera — Top');
    this.addCameraNumber(top, control, 'height', 'top height', 3, 100, 0.5);
    this.addCameraNumber(top, control, 'tiltDeg', 'top tilt (deg)', 25, 90, 1);
    this.addCameraNumber(top, control, 'topLookAhead', 'top lookahead', 0, 80, 0.5);
    this.addCameraNumber(top, control, 'topLeadResponsiveness', 'top lead response', 1, 20, 0.5);
    this.addCameraNumber(top, control, 'topLeadFullSpeed', 'lead full speed', 10, 120, 1);
    this.addCameraNumber(top, control, 'topFollowResponsiveness', 'top follow lag', 0, 30, 0.5);
    this.addCameraNumber(top, control, 'topFovDeg', 'top FOV', 25, 90, 0.5);

    const look = this.addFolder(gui, 'Drive Camera — Look Into Turn');
    this.addCameraBoolean(look, control, 'apexAimEnabled', 'apex aim on');
    this.addCameraNumber(look, control, 'apexLookAhead', 'apex base (m)', 5, 80, 1);
    this.addCameraNumber(look, control, 'apexLookAheadSpeedGain', 'apex speed gain', 0, 60, 1);
    this.addCameraNumber(look, control, 'apexMaxLateralOffset', 'apex side cap (m)', 0, 24, 0.5);
    this.addCameraNumber(look, control, 'apexOffTrackFadeStart', 'fade start widths', 0, 2, 0.05);
    this.addCameraNumber(look, control, 'apexOffTrackFadeEnd', 'fade end widths', 0, 3, 0.05);
    this.addCameraNumber(look, control, 'lookSteerBias', 'extra bias (m)', 0, 20, 0.25);
    this.addCameraNumber(look, control, 'cameraLateralLean', 'cam lean (m)', 0, 2, 0.05);
    this.addCameraNumber(look, control, 'bankMaxRollDeg', 'bank max (deg)', 0, 25, 0.5);
    this.addCameraNumber(look, control, 'bankHalflife', 'bank halflife', 0.05, 1.00, 0.01);

    for (const key of cameraNumericKeys()) {
      if (!Object.prototype.hasOwnProperty.call(control.tuning, key)) {
        control.tuning[key] = 0;
      }
    }
    for (const key of cameraBooleanKeys()) {
      if (!Object.prototype.hasOwnProperty.call(control.tuning, key)) {
        control.tuning[key] = false;
      }
    }
  }

  private addCameraNumber(
    folder: GUI,
    control: DriveCameraControl,
    key: CameraNumericKey,
    label: string,
    min: number,
    max: number,
    step: number,
  ): void {
    folder
      .add(control.tuning, key, min, max, step)
      .name(label)
      .listen()
      .onChange((value: number) => {
        control.onChange(key, value);
      });
  }

  private addCameraBoolean(
    folder: GUI,
    control: DriveCameraControl,
    key: CameraBooleanKey,
    label: string,
  ): void {
    folder
      .add(control.tuning, key)
      .name(label)
      .listen()
      .onChange((value: boolean) => {
        control.onBooleanChange?.(key, value);
      });
  }

  private addSkyTab = (gui: GUI): void => {
    this.addArtDirectionFolder(gui);
    this.addSkyDirectorFolder(gui);
    this.addSunPlacementFolder(gui);
    this.addCloudFolder(gui);
  };

  private addSfxTab = (gui: GUI): void => {
    this.addSfxFolder(gui);
  };

  private addCameraTab = (gui: GUI): void => {
    this.addMapViewCameraFolder(gui);
    this.addDriveCameraFolder(gui);
  };

  private addShadowsTab = (gui: GUI): void => {
    this.addShadowMapFolder(gui);
    this.addContactShadowFolder(gui);
  };

  private addTrackTab = (gui: GUI): void => {
    this.addPavementFolder(gui);
  };

  private addGroundTab = (gui: GUI): void => {
    this.addGroundFolder(gui);
  };

  private addDayCycleTab = (gui: GUI): void => {
    this.addDayCycleFolder(gui);
  };

  // The single source of truth for everything that contributes to scene
  // brightness. Builds top-down so the most-used dials come first: layer
  // toggles → master atmosphere → sun rig → IBL → fog → tone mapping.
  private addLightingTab = (gui: GUI): void => {
    this.addIsolateLayersFolder(gui);
    this.addMasterFolder(gui);
    this.addDirectLightFolder(gui);
    this.addIndirectFolder(gui);
    this.addFogFolder(gui);
    this.addToneMapFolder(gui);
  };

  private addIsolateLayersFolder(gui: GUI): void {
    const layers = this.opts.debugLayers;
    if (!layers) return;
    // Toggles mutate the shared `debugLayers` object in place. The render
    // loop reads it each frame via applyDebugLayers — no commit() call here,
    // so nothing is written to StyleConfig / localStorage / defaults.json.
    const all = (value: boolean) => () => {
      for (const k of Object.keys(layers) as Array<keyof DebugLayers>) {
        // 'clearToBlack' is the inverse of "is the sky in the clear" — leave
        // it alone in bulk ops so reset doesn't paint the screen black.
        if (k === 'clearToBlack') continue;
        layers[k] = value;
      }
      this.refreshControllers();
    };
    const root = this.addFolder(gui, 'Isolate Layers', true);
    root.add({ off: all(false) }, 'off').name('Everything OFF');
    root.add({ on: all(true) }, 'on').name('Everything ON');
    root.add(layers, 'clearToBlack').name('clear to black');
    root.add(layers, 'keyLight').name('key (sun)');
    root.add(layers, 'fillLight').name('fill');
    root.add(layers, 'hemisphere').name('hemisphere');
    root.add(layers, 'bounceLight').name('bounce');
    root.add(layers, 'rimLight').name('rim');
    root.add(layers, 'environment').name('IBL (scene.environment)');
    root.add(layers, 'fog').name('fog');
    root.add(layers, 'sky').name('sky dome');
    root.add(layers, 'emissive').name('emissive (engine + particles)');
    root.add(layers, 'bloom').name('bloom');
    root.add(layers, 'colorGrade').name('color grade');
    root.add(layers, 'motionBlur').name('motion blur');
    root.add(layers, 'toneMapping').name('tone mapping');
  }

  private addToneMapFolder(parent: GUI): void {
    const folder = this.addFolder(parent, 'Tone Map / Exposure', true);
    const sfx = this.opts.config.sfx;
    folder.add(sfx.colorGrade, 'toneMapping', ['None', 'ACES', 'AgX', 'Linear']).name('tone mapping').onChange(() => this.commit());
    folder.add(sfx.colorGrade, 'exposure', 0.4, 1.8, 0.01).name('exposure').onChange(() => this.commit());
  }

  // ───────────────────────────────────────────────── folder builders ──────

  private addArtDirectionFolder(parent: GUI): void {
    const folder = this.addFolder(parent, 'Art Direction', true);
    folder.add(this.opts.config.artDirection, 'preset', [
      'desertHero',
      'hotDesertNoon',
      'electricSunset',
      'purpleDusk',
      'blueMoonNight',
      'neonStorm',
    ]).name('preset').onChange((name: LightingPresetName) => this.applyPreset(name));
    const value = this.opts.config.artDirection.valueComposition;
    folder.add(value, 'fgExposure', 0.5, 1.5, 0.01).name('fg exposure').onChange(() => this.commit());
    folder.add(value, 'mgExposure', 0.5, 1.5, 0.01).name('mg exposure').onChange(() => this.commit());
    folder.add(value, 'bgExposure', 0.5, 1.5, 0.01).name('bg exposure').onChange(() => this.commit());
    folder.add({ locked: () => this.lockedDerivedLightingSummary() }, 'locked').name('preset locks').listen().disable();
  }

  private addMasterFolder(parent: GUI): void {
    const folder = this.addFolder(parent, 'Master Time of Day', true);
    const master = this.opts.config.master;
    folder.add(master.atmosphere, 'turbidity', 1, 10, 0.1).name('turbidity').onChange(() => this.commit());
    folder.add(master.atmosphere, 'hazeBoost', 0, 2, 0.01).name('haze boost').onChange(() => this.commit());
    folder.add(master.atmosphere, 'warmthBias', -1, 1, 0.01).name('warmth bias').onChange(() => this.commit());

    const status = this.addFolder(parent, 'Status', true);
    const proxy = {
      overrides: () => `${this.activeOverrideCount()} overrides`,
      compare: false,
      clearAll: () => {
        for (const override of Object.values(this.opts.config.overrides)) override.enabled = false;
        this.commit();
        this.refreshControllers();
      },
    };
    status.add(proxy, 'overrides').name('active').listen().disable();
    status.add(proxy, 'compare').name('compare solver').onChange((enabled: boolean) => {
      for (const override of Object.values(this.opts.config.overrides)) override.enabled = !enabled;
      this.commit();
      this.refreshControllers();
    });
    status.add(proxy, 'clearAll').name('Clear all overrides');
  }

  private addSkyDirectorFolder(parent: GUI): void {
    const folder = this.addFolder(parent, 'Sky Director', true);
    this.addOverrideColor(folder, 'sky.horizonHex', this.opts.config.sky, 'horizonHex', 'horizon');
    this.addOverrideColor(folder, 'sky.lowerHex', this.opts.config.sky, 'lowerHex', 'lower');
    this.addOverrideColor(folder, 'sky.midHex', this.opts.config.sky, 'midHex', 'mid');
    this.addOverrideColor(folder, 'sky.upperHex', this.opts.config.sky, 'upperHex', 'upper');
    this.addOverrideColor(folder, 'sky.zenithHex', this.opts.config.sky, 'zenithHex', 'zenith');
    this.addOverrideColor(folder, 'sky.oppositeHorizonHex', this.opts.config.sky, 'oppositeHorizonHex', 'opposite');
    this.addOverrideNumber(folder, 'sky.oppositeHorizonStrength', this.opts.config.sky, 'oppositeHorizonStrength', 'opposite amt', 0, 1, 0.01);
    this.addOverrideColor(folder, 'sky.horizonGlowHex', this.opts.config.sky, 'horizonGlowHex', 'glow color');
    this.addOverrideNumber(folder, 'sky.horizonGlowIntensity', this.opts.config.sky, 'horizonGlowIntensity', 'glow', 0, 3, 0.01);
    this.addOverrideNumber(folder, 'sky.starIntensity', this.opts.config.sky, 'starIntensity', 'stars', 0, 1, 0.01);
    this.addNullableString(folder, 'panorama URL', this.opts.config.sky.panoramaUrl, (value) => {
      this.opts.config.sky.panoramaUrl = value;
    });
  }

  private addFogFolder(parent: GUI): void {
    const folder = this.addFolder(parent, 'Atmosphere / Fog', true);
    this.addOverrideColor(folder, 'fog.colorHex', this.opts.config.fog, 'colorHex', 'haze color');
    this.addOverrideNumber(folder, 'fog.density', this.opts.config.fog, 'density', 'density', 0, 0.006, 0.00005);
    this.addOverrideBoolean(folder, 'fog.followSky', this.opts.config.fog, 'followSky', 'follow sky tint');
    folder.add(this.opts.config.fog, 'inscatterStrength', 0, 1, 0.01).name('inscatter').onChange(() => this.commitDirectValue('fog.inscatterStrength'));
    folder.add(this.opts.config.fog, 'inscatterPower', 1, 16, 0.25).name('inscatter power').onChange(() => this.commitDirectValue('fog.inscatterPower'));
    folder.addColor(this.opts.config.fog, 'warmTintHex').name('warm fog').onChange(() => this.commitDirectValue('fog.warmTintHex'));
    folder.addColor(this.opts.config.fog, 'coolTintHex').name('cool fog').onChange(() => this.commitDirectValue('fog.coolTintHex'));
  }

  // Sky-tab side of the sun: where the suns sit in the dome and how they look.
  // Sun *intensities & shadow* live in addDirectLightFolder under Lighting.
  private addSunPlacementFolder(parent: GUI): void {
    const placement = this.addFolder(parent, 'Sun Placement', true);
    placement.add(this.opts.config.master.secondarySun, 'enabled').name('sun B enabled').onChange(() => this.commit());
    placement.add(this.opts.config.master.secondarySun, 'intensity', 0, 2, 0.01).name('sun B intensity').onChange(() => this.commit());
    this.addMasterSunDirectionControls(placement, 'sun A', 'primary');
    this.addMasterSunDirectionControls(placement, 'sun B', 'secondary');

    const binary = this.addFolder(parent, 'Binary Layout');
    const updateBinary = (): void => {
      applyBinarySunLayout(this.opts.config);
      this.syncMasterFromLegacySunDirs();
      this.clearMasterSunDirectionOverrides();
      this.syncDirectionControls();
      this.commit();
      this.refreshControllers();
    };
    binary.add(this.opts.config.sky.binary, 'centerElevation', -35, 80, 1).name('center elev').onChange(updateBinary);
    binary.add(this.opts.config.sky.binary, 'centerAzimuth', -180, 180, 1).name('center azim').onChange(updateBinary);
    binary.add(this.opts.config.sky.binary, 'separationDeg', 0, 80, 1).name('separation').onChange(updateBinary);
    binary.add(this.opts.config.sky.binary, 'tiltDeg', -90, 90, 1).name('tilt').onChange(updateBinary);
    binary.add(this.opts.config.sky.binary, 'distance', 20, 220, 1).name('distance').onChange(updateBinary);

    const appearance = this.addFolder(parent, 'Sun Discs + Halos');
    appearance.add(this.opts.config.sky, 'sunDiscScale', 0.2, 4, 0.01).name('sun A disc').onChange(() => this.commit());
    appearance.add(this.opts.config.sky, 'sunHaloScale', 0, 4, 0.01).name('sun A halo').onChange(() => this.commit());
    appearance.add(this.opts.config.sky, 'secondarySunDiscScale', 0.2, 4, 0.01).name('sun B disc').onChange(() => this.commit());
    appearance.add(this.opts.config.sky, 'secondarySunHaloScale', 0, 4, 0.01).name('sun B halo').onChange(() => this.commit());
  }

  // Lighting-tab side of the sun: the five-light rig + shadow toggle.
  private addDirectLightFolder(parent: GUI): void {
    const folder = this.addFolder(parent, 'Direct Light + Shadow', true);
    this.addOverrideColor(folder, 'sunRig.key.colorHex', this.opts.config.sunRig.key, 'colorHex', 'key color');
    this.addOverrideNumber(folder, 'sunRig.key.intensity', this.opts.config.sunRig.key, 'intensity', 'key intensity', 0, 8, 0.05);
    folder.add(this.opts.config.sunRig.key, 'minElevationDeg', -35, 35, 1).name('key min elev').onChange(() => this.commit());
    folder.add(this.opts.config.sunRig.key, 'elevationOffsetDeg', -25, 25, 1).name('key elev off').onChange(() => this.commit());
    folder.add(this.opts.config.sunRig.key, 'azimuthOffsetDeg', -45, 45, 1).name('key azim off').onChange(() => this.commit());
    folder.add(this.opts.config.sunRig.key, 'castShadow').name('key shadows').onChange(() => this.commit());
    this.addOverrideColor(folder, 'sunRig.fill.colorHex', this.opts.config.sunRig.fill, 'colorHex', 'fill color');
    this.addOverrideNumber(folder, 'sunRig.fill.intensity', this.opts.config.sunRig.fill, 'intensity', 'fill intensity', 0, 3, 0.05);
    folder.add(this.opts.config.sunRig.fill, 'minElevationDeg', -35, 35, 1).name('fill min elev').onChange(() => this.commit());
  }

  // Hemi + bounce + rim + IBL probe (the latter is the dominant cheap-fill
  // contributor, dialed by sunRig.environmentIntensity). Range goes to 2.0
  // because at the bottom end it can be too dim and the user wanted finer
  // control over how much work the probe does.
  private addIndirectFolder(parent: GUI): void {
    const hemi = this.addFolder(parent, 'Hemisphere / Bounce / Rim', true);
    hemi.addColor(this.opts.config.sunRig.hemisphere, 'skyColorHex').name('hemi sky').onChange(() => this.commitDirectValue('sunRig.hemisphere.skyColorHex'));
    hemi.addColor(this.opts.config.sunRig.hemisphere, 'groundColorHex').name('hemi ground').onChange(() => this.commitDirectValue('sunRig.hemisphere.groundColorHex'));
    hemi.add(this.opts.config.sunRig.hemisphere, 'intensity', 0, 2, 0.01).name('hemi intensity').onChange(() => this.commitDirectValue('sunRig.hemisphere.intensity'));
    hemi.addColor(this.opts.config.sunRig.bounce, 'colorHex').name('bounce color').onChange(() => this.commitDirectValue('sunRig.bounce.colorHex'));
    hemi.add(this.opts.config.sunRig.bounce, 'intensity', 0, 2, 0.01).name('bounce').onChange(() => this.commitDirectValue('sunRig.bounce.intensity'));
    hemi.add(this.opts.config.sunRig.bounce, 'elevationDeg', -60, 40, 1).name('bounce elev').onChange(() => this.commitDirectValue('sunRig.bounce.elevationDeg'));
    hemi.add(this.opts.config.sunRig.bounce, 'azimuthDeg', -180, 180, 1).name('bounce azim').onChange(() => this.commitDirectValue('sunRig.bounce.azimuthDeg'));
    hemi.addColor(this.opts.config.sunRig.rim, 'colorHex').name('rim color').onChange(() => this.commitDirectValue('sunRig.rim.colorHex'));
    hemi.add(this.opts.config.sunRig.rim, 'intensity', 0, 2, 0.01).name('rim').onChange(() => this.commitDirectValue('sunRig.rim.intensity'));
    hemi.add(this.opts.config.sunRig.rim, 'cameraAnchored').name('camera rim').onChange(() => this.commitDirectValue('sunRig.rim.cameraAnchored'));
    hemi.add(this.opts.config.sunRig.rim.cameraSpaceDir, 'x', -1, 1, 0.01).name('rim cam x').onChange(() => this.commitDirectValue('sunRig.rim.cameraSpaceDir'));
    hemi.add(this.opts.config.sunRig.rim.cameraSpaceDir, 'y', -1, 1, 0.01).name('rim cam y').onChange(() => this.commitDirectValue('sunRig.rim.cameraSpaceDir'));
    hemi.add(this.opts.config.sunRig.rim.cameraSpaceDir, 'z', -1, 1, 0.01).name('rim cam z').onChange(() => this.commitDirectValue('sunRig.rim.cameraSpaceDir'));
    hemi.add(this.opts.config.sunRig.rim, 'elevationDeg', -20, 60, 1).name('rim elev (world rim only)').onChange(() => this.commitDirectValue('sunRig.rim.elevationDeg'));
    hemi.add(this.opts.config.sunRig.rim, 'azimuthDeg', -180, 180, 1).name('rim azim (world rim only)').onChange(() => this.commitDirectValue('sunRig.rim.azimuthDeg'));

    // The IBL probe (Scene.environment) is broken out into its own folder so
    // it's discoverable. It's the contributor most likely to "do too much
    // work" because it ambient-lights every PBR material in the scene at
    // once — every glossy surface picks it up unless dialed.
    const ibl = this.addFolder(parent, 'IBL (scene.environment)', true);
    ibl.add(this.opts.config.sunRig, 'environmentIntensity', 0, 2, 0.01).name('IBL intensity').onChange(() => this.commitDirectValue('sunRig.environmentIntensity'));
  }

  private addCloudFolder(parent: GUI): void {
    this.addCloudShadingFolder(parent);
    this.addCloudLayerFolder(parent, 'Clouds — Low', 'low');
    this.addCloudLayerFolder(parent, 'Clouds — Mid', 'mid');
    this.addCloudLayerFolder(parent, 'Clouds — High', 'high');
  }

  private addCloudShadingFolder(parent: GUI): void {
    const folder = this.addFolder(parent, 'Clouds — Shading', true);
    const shading = this.opts.config.clouds.shading;
    folder.addColor(shading, 'cloudBaseHex').name('base color').onChange(() => this.commit());
    folder.add(shading, 'wrap', 0, 1, 0.01).name('shadow wrap').onChange(() => this.commit());
    folder.add(shading, 'rimStrength', 0, 2, 0.01).name('rim strength').onChange(() => this.commit());
  }

  private addCloudLayerFolder(parent: GUI, label: string, name: 'low' | 'mid' | 'high'): void {
    const folder = this.addFolder(parent, label);
    const layer = this.opts.config.clouds.layers[name];
    folder.add(layer, 'enabled').onChange(() => this.commit());
    folder.add(layer, 'count', 0, 80, 1).onFinishChange(() => this.commit());
    folder.add(layer, 'altitude', 0, 400, 1).onFinishChange(() => this.commit());
    folder.add(layer, 'domeRadius', 120, 1500, 10).onFinishChange(() => this.commit());
    folder.add(layer, 'coverage', 0, 1, 0.01).onFinishChange(() => this.commit());
    folder.add(layer, 'scale', 0.3, 3, 0.05).onFinishChange(() => this.commit());
    folder.add(layer, 'shape', ['ribbon', 'wisp', 'puff', 'bank']).name('shape').onFinishChange(() => this.commit());
    folder.add(layer, 'width', 40, 360, 1).name('width').onFinishChange(() => this.commit());
    folder.add(layer, 'height', 8, 140, 1).name('height').onFinishChange(() => this.commit());
    folder.add(layer, 'elongation', 0.6, 6, 0.05).name('elongation').onFinishChange(() => this.commit());
    folder.add(layer, 'edgeSoftness', 0.05, 0.85, 0.01).name('edge softness').onFinishChange(() => this.commit());
    folder.add(layer, 'noiseScale', 0.5, 8, 0.05).name('breakup scale').onFinishChange(() => this.commit());
    folder.add(layer, 'noiseStrength', 0, 1, 0.01).name('breakup').onFinishChange(() => this.commit());
    folder.add(layer, 'opacity', 0, 1, 0.01).name('opacity').onFinishChange(() => this.commit());
    folder.add(layer, 'normalStrength', 0, 1, 0.01).name('fake volume').onFinishChange(() => this.commit());
    folder.add(layer, 'curl', 0, 1, 0.01).name('curl').onFinishChange(() => this.commit());
    folder.add(layer, 'windShear', 0, 1, 0.01).name('wind shear').onFinishChange(() => this.commit());
    folder.add(layer, 'fogEnabled').name('fogged').onChange(() => this.commit());
    folder.add(layer, 'rotateSpeed', -0.02, 0.02, 0.0005).onChange(() => this.commit());
  }

  private addPavementFolder(parent: GUI): void {
    const asphalt = this.opts.config.asphalt;

    const base = this.addFolder(parent, 'Asphalt — Base', true);
    base.addColor(asphalt, 'baseHex').name('base').onChange(() => this.commit());
    base.add(asphalt, 'baseRoughness', 0.3, 1.0, 0.01).name('base roughness').onChange(() => this.commit());
    base.add(asphalt, 'baseEnvIntensity', 0, 2, 0.01).name('base env reflection').onChange(() => this.commit());

    const grain = this.addFolder(parent, 'Asphalt — Grain');
    grain.addColor(asphalt, 'aggregateColorHex').name('aggregate color').onChange(() => this.commit());
    grain.add(asphalt, 'aggregateIntensity', 0, 1, 0.01).name('aggregate intensity').onChange(() => this.commit());
    // Regenerates the 512² DataTexture, so commit on finish to avoid thrashing during a drag.
    grain.add(asphalt, 'aggregateScale', 0.2, 4, 0.05).name('scale').onFinishChange(() => this.commit());
    // grainTileMeters / grainDetileMix only rewrite uniforms — cheap, commit on change.
    grain.add(asphalt, 'grainTileMeters', 2, 32, 0.5).name('tile meters').onChange(() => this.commit());
    grain.add(asphalt, 'grainDetileMix', 0, 1, 0.01).name('detile mix').onChange(() => this.commit());
    // grainElongation regenerates the DataTexture; commit on finish.
    grain.add(asphalt, 'grainElongation', 0.4, 3, 0.05).name('fiber elongation').onFinishChange(() => this.commit());

    const rubber = this.addFolder(parent, 'Asphalt — Rubber Streaks');
    rubber.addColor(asphalt, 'rubberStreakHex').name('rubber color').onChange(() => this.commit());
    rubber.add(asphalt, 'rubberStreakIntensity', 0, 4, 0.01).name('rubber intensity').onChange(() => this.commit());
    rubber.add(asphalt, 'rubberStreakLongScale', 0.02, 1, 0.005).name('rubber long scale').onChange(() => this.commit());
    rubber.add(asphalt, 'rubberStreakLateralFreq', 1, 20, 0.1).name('lateral freq').onChange(() => this.commit());
    rubber.add(asphalt, 'rubberStreakLaneWidth', 0.02, 0.8, 0.005).name('lane width').onChange(() => this.commit());
    rubber.add(asphalt, 'rubberStreakThreshold', 0, 0.95, 0.01).name('rubber threshold').onChange(() => this.commit());

    const oil = this.addFolder(parent, 'Asphalt — Oil Sheen');
    oil.addColor(asphalt, 'oilSheenHex').name('oil color').onChange(() => this.commit());
    oil.add(asphalt, 'oilSheenIntensity', 0, 1, 0.01).name('oil intensity').onChange(() => this.commit());
    oil.add(asphalt, 'oilSheenLongScale', 0.05, 0.5, 0.005).name('oil long scale').onChange(() => this.commit());
    oil.add(asphalt, 'oilSheenThreshold', 0, 0.7, 0.01).name('oil threshold').onChange(() => this.commit());

    const wear = this.addFolder(parent, 'Asphalt — Wear');
    wear.add(asphalt, 'centerWear', 0, 0.6, 0.01).name('center wear').onChange(() => this.commit());
    wear.add(asphalt, 'edgeDebris', 0, 0.12, 0.005).name('edge debris').onChange(() => this.commit());

    const cracks = this.addFolder(parent, 'Asphalt — Cracks');
    cracks.addColor(asphalt.cracks, 'colorHex').name('crack color').onChange(() => this.commit());
    cracks.add(asphalt.cracks, 'intensity', 0, 1, 0.01).name('crack intensity').onChange(() => this.commit());
    cracks.add(asphalt.cracks, 'fineChecking', 0, 1, 0.01).name('fine checking').onChange(() => this.commit());
    cracks.add(asphalt.cracks, 'majorCount', 0, 22, 1).name('major cracks').onFinishChange(() => this.commit());
    cracks.add(asphalt.cracks, 'branchiness', 0, 1, 0.01).name('branchiness').onFinishChange(() => this.commit());
    cracks.add(asphalt.cracks, 'transverseBias', 0, 1, 0.01).name('cross-track bias').onFinishChange(() => this.commit());
    cracks.add(asphalt.cracks, 'cornerBias', 0, 1, 0.01).name('corner bias').onFinishChange(() => this.commit());
    cracks.add(asphalt.cracks, 'edgeBias', 0, 1, 0.01).name('edge bias').onFinishChange(() => this.commit());
    cracks.add(asphalt.cracks, 'maxLength', 3, 34, 0.5).name('max length').onFinishChange(() => this.commit());
    cracks.add(asphalt.cracks, 'width', 0.02, 0.4, 0.005).name('crack width').onChange(() => this.commit());
    cracks.add(asphalt.cracks, 'depth', 0, 0.6, 0.005).name('crack depth').onChange(() => this.commit());
    cracks.add(asphalt.cracks, 'seed', 1, 9999, 1).name('crack seed').onFinishChange(() => this.commit());

    // Names are deliberately unique across the whole panel so the tuner — and
    // the AsphaltControls browser test — can find each slider by visible label.
    // "intensity" alone collides with master/sun/oil/aggregate intensity, so
    // glint controls are prefixed with "glint".
    const glint = this.addFolder(parent, 'Asphalt — Sun Glint');
    glint.addColor(this.opts.config.trackGlint, 'colorHex').name('glint color').onChange(() => this.commit());
    glint.add(this.opts.config.trackGlint, 'intensity', 0, 12, 0.01).name('glint intensity').onChange(() => this.commit());
    glint.add(this.opts.config.trackGlint, 'exponent', 1, 320, 1).name('glint exponent').onChange(() => this.commit());
    glint.add(this.opts.config.trackGlint, 'streakScale', 0.05, 16, 0.05).name('glint streak scale').onChange(() => this.commit());
    glint.add(this.opts.config.trackGlint, 'broadness', 0, 3, 0.01).name('glint broadness').onChange(() => this.commit());
    glint.add(this.opts.config.trackGlint, 'textureInfluence', 0, 2, 0.01).name('glint texture mix').onChange(() => this.commit());
    glint.add(this.opts.config.trackGlint, 'normalStrength', 0, 8, 0.01).name('glint bump strength').onChange(() => this.commit());
    glint.add(this.opts.config.trackGlint, 'roughnessContrast', 0, 2, 0.01).name('glint roughness').onChange(() => this.commit());

    // WebGPU-only tuning. WebGL ignores everything here, so dialing in the
    // WebGPU look cannot regress the WebGL appearance. Useful because the
    // TSL composition reads slightly differently from the WebGL
    // onBeforeCompile path (anisotropic sampling, dpdx tangent derivation,
    // tonemap interaction) and the same scalar values produce subtly
    // different output. Add fields here ONLY when WebGL/WebGPU genuinely
    // diverge — for everything else, the shared sliders above still drive
    // both backends through applyWebGPUAsphaltStyle.
    const webgpu = this.addFolder(parent, 'WebGPU tuning (does not affect WebGL)');
    webgpu.add(this.opts.config.trackGlint, 'webgpuBumpMultiplier', 0, 4, 0.01)
      .name('webgpu bump ×').onChange(() => this.commit());
    webgpu.add(this.opts.config.asphalt, 'webgpuAggregateMultiplier', 0, 4, 0.01)
      .name('webgpu aggregate ×').onChange(() => this.commit());
    this.addRendererBackendSelector(webgpu);

    const edge = this.addFolder(parent, 'Edge Lines');
    edge.addColor(this.opts.config.edgeLines, 'colorHex').name('edge color').onChange(() => this.commit());
    edge.add(this.opts.config.edgeLines, 'width', 0.18, 0.9, 0.01).onFinishChange(() => this.commit());
    edge.add(this.opts.config.edgeLines, 'emissiveStrength', 0, 1, 0.01).onChange(() => this.commit());

    const kerbs = this.addFolder(parent, 'Kerbs');
    kerbs.add(this.opts.config.kerbs, 'enabled').onChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'continuous').onFinishChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'baseWidth', 0.6, 6, 0.05).onFinishChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'cornerWidthBoost', 0, 5, 0.05).onFinishChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'stripeLength', 0.8, 5, 0.05).onFinishChange(() => this.commit());
    kerbs.addColor(this.opts.config.kerbs, 'redHex').name('red').onChange(() => this.commit());
    kerbs.addColor(this.opts.config.kerbs, 'whiteHex').name('white').onChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'height', 0.01, 0.24, 0.005).onFinishChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'ridgeStrength', 0, 1.5, 0.01).name('ridge strength').onChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'rubberInfluence', 0, 1, 0.01).name('rubber influence').onChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'roughness', 0, 1, 0.01).onChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'metalness', 0, 1, 0.01).onChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'envMapIntensity', 0, 3, 0.01).name('env map intensity').onChange(() => this.commit());
    kerbs.addColor(this.opts.config.kerbs, 'emissiveHex').name('emissive').onChange(() => this.commit());
    kerbs.add(this.opts.config.kerbs, 'emissiveIntensity', 0, 2, 0.01).name('emissive intensity').onChange(() => this.commit());

    const furniture = this.addFolder(parent, 'Track Furniture');
    furniture.add(this.opts.config.furniture, 'enabled').onChange(() => this.commit());
    furniture.add(this.opts.config.furniture, 'chevronsEnabled').name('chevrons').onFinishChange(() => this.commit());
    furniture.add(this.opts.config.furniture, 'railsEnabled').name('rails').onFinishChange(() => this.commit());
    furniture.add(this.opts.config.furniture, 'bannersEnabled').name('banners').onFinishChange(() => this.commit());
    this.addNullableString(furniture, 'banner GLB', this.opts.config.furniture.bannerGlbUrl, (value) => {
      this.opts.config.furniture.bannerGlbUrl = value;
    });
    furniture.add(this.opts.config.furniture, 'spacing', 30, 180, 1).onFinishChange(() => this.commit());
    furniture.add(this.opts.config.furniture, 'cornerDensity', 0, 1, 0.01).onFinishChange(() => this.commit());
  }

  // Sand surface around the track. Palette + saturation drive the vertex
  // colour bake (re-uploaded on commit), while the rest are pure shader
  // uniforms — moving them is free past the first draw.
  private addGroundFolder(parent: GUI): void {
    const ground = this.opts.config.ground;

    const palette = this.addFolder(parent, 'Ground — Palette', true);
    palette.addColor(ground, 'shadowHex').name('shadow').onChange(() => this.commit());
    palette.addColor(ground, 'valleyHex').name('valley').onChange(() => this.commit());
    palette.addColor(ground, 'sandHex').name('sand').onChange(() => this.commit());
    palette.addColor(ground, 'ridgeHex').name('ridge').onChange(() => this.commit());
    palette.addColor(ground, 'peakHex').name('peak').onChange(() => this.commit());
    palette.add(ground, 'saturationBoost', 0, 2, 0.01).name('saturation boost').onChange(() => this.commit());

    const ripples = this.addFolder(parent, 'Ground — Wind Ripples');
    ripples.add(ground, 'rippleIntensity', 0, 1, 0.01).name('ripple intensity').onChange(() => this.commit());
    ripples.add(ground, 'rippleScaleU', 4, 240, 1).name('ripple scale (along)').onChange(() => this.commit());
    ripples.add(ground, 'rippleScaleV', 1, 60, 0.5).name('ripple scale (across)').onChange(() => this.commit());
    ripples.add(ground, 'rippleAngleDeg', 0, 360, 1).name('wind angle (deg)').onChange(() => this.commit());

    const detail = this.addFolder(parent, 'Ground — Surface Detail');
    detail.add(ground, 'speckleIntensity', 0, 0.5, 0.01).name('speckle intensity').onChange(() => this.commit());
    detail.add(ground, 'speckleDensity', 0.7, 0.99, 0.005).name('speckle density').onChange(() => this.commit());
    detail.add(ground, 'patchIntensity', 0, 0.4, 0.01).name('patch intensity').onChange(() => this.commit());
    detail.add(ground, 'patchScale', 0.005, 0.2, 0.005).name('patch scale').onChange(() => this.commit());

    const glint = this.addFolder(parent, 'Ground — Sun Glint');
    glint.addColor(ground, 'glintColorHex').name('glint color').onChange(() => this.commit());
    glint.add(ground, 'glintIntensity', 0, 1, 0.01).name('glint intensity').onChange(() => this.commit());
    glint.add(ground, 'glintExponent', 4, 64, 0.5).name('glint exponent').onChange(() => this.commit());
  }

  private addSfxFolder(parent: GUI): void {
    const { sfx } = this.opts.config;

    const bloom = this.addFolder(parent, 'Bloom', true);
    bloom.add(sfx.bloom, 'intensity', 0, 3, 0.01).name('bloom intensity').onChange(() => this.commit());
    bloom.add(sfx.bloom, 'radius', 0, 1, 0.01).name('bloom radius').onChange(() => this.commit());
    bloom.add(sfx.bloom, 'threshold', 0, 3, 0.01).name('bloom threshold').onChange(() => this.commit());

    const selective = this.addFolder(parent, 'Selective Bloom');
    selective.add(sfx.selectiveBloom, 'enabled').name('selective bloom enabled').onChange(() => this.commit());
    selective.add(sfx.selectiveBloom, 'intensity', 0, 3, 0.01).name('selective bloom intensity').onChange(() => this.commit());

    // Tone mapping + exposure live in the Lighting tab (they belong with the
    // light contributors that feed them), so this folder only carries the
    // grade-curve params: lift/gain/contrast/gamma/saturation/LUT.
    const grade = this.addFolder(parent, 'Color Grade', true);
    grade.add(sfx.colorGrade, 'enabled').name('color grade').onChange(() => this.commit());
    grade.add(sfx.colorGrade.lift, 'r', -0.2, 0.2, 0.001).name('lift r').onChange(() => this.commit());
    grade.add(sfx.colorGrade.lift, 'g', -0.2, 0.2, 0.001).name('lift g').onChange(() => this.commit());
    grade.add(sfx.colorGrade.lift, 'b', -0.2, 0.2, 0.001).name('lift b').onChange(() => this.commit());
    grade.add(sfx.colorGrade.gain, 'r', 0.5, 1.8, 0.001).name('gain r').onChange(() => this.commit());
    grade.add(sfx.colorGrade.gain, 'g', 0.5, 1.8, 0.001).name('gain g').onChange(() => this.commit());
    grade.add(sfx.colorGrade.gain, 'b', 0.5, 1.8, 0.001).name('gain b').onChange(() => this.commit());
    grade.add(sfx.colorGrade, 'contrast', 0.5, 1.8, 0.01).name('pivoted contrast').onChange(() => this.commit());
    grade.add(sfx.colorGrade, 'gamma', 0.85, 1.15, 0.005).name('gamma').onChange(() => this.commit());
    grade.add(sfx.colorGrade, 'saturation', 0, 2, 0.01).name('saturation').onChange(() => this.commit());
    this.addNullableString(grade, 'LUT URL', sfx.colorGrade.lutUrl, (value) => {
      sfx.colorGrade.lutUrl = value;
    });

    const vignette = this.addFolder(parent, 'Vignette');
    vignette.add(sfx.vignette, 'strength', 0, 0.8, 0.01).name('vignette strength').onChange(() => this.commit());
    vignette.add(sfx.vignette, 'softness', 0.05, 1.2, 0.01).name('vignette softness').onChange(() => this.commit());
    vignette.addColor(sfx.vignette, 'tintHex').name('vignette tint').onChange(() => this.commit());

    const chroma = this.addFolder(parent, 'Chromatic Aberration');
    chroma.add(sfx.chroma, 'amount', 0, 0.01, 0.0001).name('chroma amount').onChange(() => this.commit());
    chroma.add(sfx.chroma, 'falloff', 0.2, 4, 0.05).name('chroma falloff').onChange(() => this.commit());
    chroma.add(sfx.chroma, 'asymmetry', 0, 2, 0.01).name('chroma asymmetry').onChange(() => this.commit());

    const motion = this.addFolder(parent, 'Motion / Radial Blur');
    motion.add(sfx.motion, 'enabled').name('motion enabled').onChange(() => this.commit());
    motion.add(sfx.motion, 'autoFromCamera').name('motion auto').onChange(() => this.commit());
    motion.add(sfx.motion, 'radialStrength', 0, 1, 0.01).name('motion strength').onChange(() => this.commit());
    motion.add(sfx.motion, 'fullSpeed', 5, 200, 1).name('motion full speed').onChange(() => this.commit());
    motion.add(sfx.motion, 'fakeSpeed', 0, 1.4, 0.01).name('motion preview').onChange(() => this.commit());
    motion.add(sfx.motion, 'falloff', 0.5, 4, 0.05).name('motion falloff').onChange(() => this.commit());
    motion.add(sfx.motion, 'chromaticAmount', 0, 0.02, 0.0005).name('motion chromatic').onChange(() => this.commit());
    motion.add(sfx.motion, 'radialSamples', [4, 8, 16]).name('motion samples').onChange((v: string | number) => {
      sfx.motion.radialSamples = Number(v) as 4 | 8 | 16;
      this.commit();
    });
    motion.add(sfx.motion, 'pavementMaskOnly').name('motion mask').disable();
  }

  private addDayCycleFolder(parent: GUI): void {
    const day = this.opts.config.dayCycle;
    const folder = this.addFolder(parent, 'Day Cycle', true);
    folder.add(day, 'enabled').onChange(() => this.commit());
    folder.add(day, 'playing').name('play').onChange(() => this.commit());
    folder.add(day, 'scrub', 0, 1, 0.001).onChange(() => this.commit());
    folder.add(day, 'durationSec', 5, 240, 1).name('duration sec').onChange(() => this.commit());
    const state = { selected: 0 };
    folder.add(state, 'selected', 0, Math.max(0, day.keyframes.length - 1), 1).name('selected key').onChange(() => this.refreshControllers());
    const actions = {
      addKey: () => {
        day.keyframes.push({
          id: `key-${Date.now().toString(36)}`,
          name: `Key ${day.keyframes.length + 1}`,
          sunElevationDeg: this.opts.config.master.sunElevationDeg,
          master: cloneStyleConfig(this.opts.config).master,
          overrides: cloneStyleConfig(this.opts.config).overrides,
        });
        state.selected = day.keyframes.length - 1;
        this.commit();
        this.refreshControllers();
      },
      saveKey: () => {
        const key = day.keyframes[state.selected];
        if (!key) return;
        key.sunElevationDeg = this.opts.config.master.sunElevationDeg;
        key.master = cloneStyleConfig(this.opts.config).master;
        key.overrides = cloneStyleConfig(this.opts.config).overrides;
        this.commit();
      },
      jumpToKey: () => {
        const key = day.keyframes[state.selected];
        if (!key) return;
        Object.assign(this.opts.config.master, cloneStyleConfig({ ...this.opts.config, master: key.master }).master);
        this.opts.config.overrides = cloneStyleConfig({ ...this.opts.config, overrides: key.overrides }).overrides;
        this.commit();
        this.refreshControllers();
      },
      deleteKey: () => {
        if (day.keyframes.length <= 1) return;
        day.keyframes.splice(state.selected, 1);
        state.selected = Math.min(state.selected, day.keyframes.length - 1);
        this.commit();
        this.refreshControllers();
      },
    };
    folder.add(actions, 'addKey').name('Add keyframe');
    folder.add(actions, 'saveKey').name('Save current to key');
    folder.add(actions, 'jumpToKey').name('Jump to key');
    folder.add(actions, 'deleteKey').name('Delete key');
  }

  // ───────────────────────────────────────────────── helpers ──────

  private setActiveTab(key: TabKey): void {
    if (this.activeTab === key) return;
    this.activeTab = key;
    writeActiveTab(key);
    this.applyActiveTab();
  }

  private applyActiveTab(): void {
    for (const [key, gui] of this.guis) {
      gui.domElement.style.display = key === this.activeTab ? '' : 'none';
    }
    this.tabRail?.setActive(this.activeTab);
  }

  private async persistToSource(): Promise<void> {
    const preset = this.opts.preset;
    const result = preset && !preset.isFallback && preset.name
      ? await saveStyleToPreset(preset.name, this.opts.config)
      : await saveStyleConfigToSource(this.opts.config);
    if (result.ok) {
      const target = preset && !preset.isFallback && preset.name
        ? `preset "${preset.label || preset.name}"`
        : 'StyleConfig.defaults.json';
      this.opts.onSaveStatus?.(`Saved to ${target} (${result.bytes ?? '?'}b)`, true);
    } else {
      this.opts.onSaveStatus?.(`Save failed: ${result.error ?? 'unknown error'}`, false);
    }
  }

  private buildSaveBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'style-panel-savebar';

    const label = document.createElement('div');
    label.className = 'style-panel-template';
    const preset = this.opts.preset;
    const templateName = preset && !preset.isFallback && preset.name
      ? `${preset.label || preset.name} (${preset.name})`
      : 'Defaults (unbound track)';
    label.textContent = `Template: ${templateName}`;

    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.action = 'save-style';
    button.textContent = 'Save to code';
    button.disabled = !isSaveToSourceAvailable();
    button.addEventListener('click', () => {
      void this.persistToSource();
    });

    bar.append(label, button);
    return bar;
  }

  private addShadowMapFolder(parent: GUI): void {
    const targets = this.opts.shadows;
    if (!targets) return;
    const { sun, shadowConfig: cfg, applyShadowConfig } = targets;
    const folder = this.addFolder(parent, 'Shadow Map', true);

    const density = { metersPerTexel: this.computeShadowTexelDensity(cfg) };
    const refreshDensity = (): void => {
      density.metersPerTexel = this.computeShadowTexelDensity(cfg);
      folder.controllersRecursive().forEach((c) => c.updateDisplay());
    };

    folder.add(cfg, 'enabled').name('shadows on').onChange(() => {
      sun.castShadow = cfg.enabled;
    });
    folder
      .add(cfg, 'frustumSize', 30, 300, 5)
      .name('frustum (±m)')
      .onChange(() => {
        sun.shadow.camera.left = -cfg.frustumSize;
        sun.shadow.camera.right = cfg.frustumSize;
        sun.shadow.camera.top = cfg.frustumSize;
        sun.shadow.camera.bottom = -cfg.frustumSize;
        sun.shadow.camera.updateProjectionMatrix();
        refreshDensity();
      });
    folder
      .add(cfg, 'far', 80, 600, 5)
      .name('far plane (m)')
      .onChange(() => {
        sun.shadow.camera.far = cfg.far;
        sun.shadow.camera.updateProjectionMatrix();
      });
    folder
      .add(cfg, 'mapSize', { '512': 512, '1024': 1024, '2048': 2048, '4096': 4096 })
      .name('map size')
      .onChange(() => {
        applyShadowConfig();
        refreshDensity();
      });
    folder
      .add(cfg, 'softRadius', 0, 4, 0.1)
      .name('PCF radius')
      .onChange(() => {
        sun.shadow.radius = cfg.softRadius;
      });
    folder.add(cfg, 'forwardBias', 0, 200, 1).name('forward bias (m)');
    folder.add(density, 'metersPerTexel').name('m / texel').listen().disable();
  }

  private computeShadowTexelDensity(cfg: { frustumSize: number; mapSize: number }): number {
    const mapSize = cfg.mapSize > 0 ? cfg.mapSize : 1;
    return Number(((cfg.frustumSize * 2) / mapSize).toFixed(3));
  }

  private addContactShadowFolder(parent: GUI): void {
    const targets = this.opts.shadows;
    if (!targets) return;
    const cs = targets.contactShadow;
    const folder = this.addFolder(parent, 'Contact Shadow', true);
    folder.add(cs.config, 'enabled').name('enabled');
    folder.add(cs.config, 'radius', 0.5, 6, 0.05).name('radius (m)');
    folder.add(cs.config, 'opacity', 0, 1, 0.01).name('opacity');
    folder.add(cs.config, 'fadeHeight', 0.2, 8, 0.1).name('fade height (m)');
    folder.add(cs.config, 'falloffPower', 0.5, 6, 0.1).name('falloff');
    folder.add(cs.config, 'groundOffset', 0, 0.2, 0.005).name('ground offset');
  }

  private addFolder(parent: GUI, name: string, openDefault = false): GUI {
    const folder = parent.addFolder(name);
    const state = readFolderState();
    const shouldOpen = state[name] ?? openDefault;
    if (shouldOpen) folder.open();
    else folder.close();
    folder.onOpenClose((gui) => {
      const next = readFolderState();
      next[name] = !gui._closed;
      writeFolderState(next);
    });
    return folder;
  }

  private addRendererBackendSelector(folder: GUI): void {
    // Switching the renderer cannot be live — the WebGL2 and WebGPU
    // backends are built once at boot, so this writes the preference to
    // localStorage (via setRendererBackendPreference) and reloads the page.
    // The current diagnostics readout is shown so it's obvious which
    // backend is actually active right now versus the requested preference.
    const proxy: { backend: RendererBackendPreference; current: string } = {
      backend: readRendererBackendPreference(),
      current: this.readActiveRendererMode(),
    };
    folder.add(proxy, 'current').name('active renderer').disable();
    folder
      .add(proxy, 'backend', ['auto', 'webgl2', 'webgpu'])
      .name('renderer (reload)')
      .onChange((value: RendererBackendPreference) => {
        setRendererBackendPreference(value);
        // Drop any ?webgl / ?webgpu URL flag so the stored preference wins
        // on reload; otherwise the flag would silently override the picker.
        const url = new URL(window.location.href);
        url.searchParams.delete('webgl');
        url.searchParams.delete('webgpu');
        window.location.replace(url.toString());
      });
  }

  private readActiveRendererMode(): string {
    const twin = (window as unknown as {
      __twin?: { diagnostics?: () => { renderer?: { mode?: string; fallbackReason?: string | null } } };
    }).__twin;
    const info = twin?.diagnostics?.().renderer;
    if (!info?.mode) return 'unknown';
    return info.fallbackReason ? `${info.mode} (fallback: ${info.fallbackReason})` : info.mode;
  }

  private commit(): void {
    if (this.opts.preset && !this.opts.preset.isFallback && this.opts.preset.name) {
      savePresetOverlay(this.opts.preset.name, this.opts.config);
    } else {
      saveStyleConfig(this.opts.config);
    }
    this.opts.onChange?.(this.opts.config);
    dispatchStyleConfigChange(this.opts.config);
  }

  private commitDirectValue(path: OverridePath): void {
    clearOverride(this.opts.config, path);
    this.syncOverrideToggleProxies();
    this.commit();
    this.refreshControllers();
  }

  private addOverrideColor<T extends object>(
    folder: GUI,
    path: OverridePath,
    target: T,
    key: keyof T & string,
    label: string,
  ): void {
    this.addOverrideToggle(folder, path, label);
    folder.addColor(target, key).name(label).onChange(() => this.commitOverrideValue(path, (target as Record<string, unknown>)[key]));
  }

  private addOverrideNumber<T extends object>(
    folder: GUI,
    path: OverridePath,
    target: T,
    key: keyof T & string,
    label: string,
    min: number,
    max: number,
    step: number,
  ): void {
    this.addOverrideToggle(folder, path, label);
    folder.add(target, key, min, max, step).name(label).onChange(() => this.commitOverrideValue(path, (target as Record<string, unknown>)[key]));
  }

  private addOverrideBoolean<T extends object>(
    folder: GUI,
    path: OverridePath,
    target: T,
    key: keyof T & string,
    label: string,
  ): void {
    this.addOverrideToggle(folder, path, label);
    folder.add(target, key).name(label).onChange(() => this.commitOverrideValue(path, (target as Record<string, unknown>)[key]));
  }

  private addOverrideToggle(folder: GUI, path: OverridePath, label: string): void {
    const proxy = { enabled: isOverridden(this.opts.config, path) };
    this.overrideToggleProxies.set(path, proxy);
    folder.add(proxy, 'enabled').name(`override ${label}`).onChange((enabled: boolean) => {
      if (enabled) {
        setOverride(this.opts.config, path, getPath(resolveStyle(this.opts.config), path));
      } else {
        clearOverride(this.opts.config, path);
      }
      this.commit();
      this.refreshControllers();
    });
  }

  private commitOverrideValue(path: OverridePath, value: unknown): void {
    setOverride(this.opts.config, path, value);
    this.syncOverrideToggleProxies();
    this.commit();
    this.refreshControllers();
  }

  private activeOverrideCount(): number {
    return Object.values(this.opts.config.overrides).filter((override) => override.enabled).length;
  }

  private lockedDerivedLightingSummary(): string {
    const names = Object.entries(this.opts.config.overrides)
      .filter(([path, override]) => path.startsWith('derivedLighting.') && override.enabled)
      .map(([path]) => path.slice('derivedLighting.'.length));
    return names.length > 0 ? names.join(', ') : 'none';
  }

  private syncOverrideToggleProxies(): void {
    for (const [path, proxy] of this.overrideToggleProxies) {
      proxy.enabled = isOverridden(this.opts.config, path);
    }
  }

  private addMasterSunDirectionControls(
    folder: GUI,
    label: string,
    sun: 'primary' | 'secondary',
  ): void {
    const proxy = this.masterSunAngles(sun);
    this.directionSyncers.push(() => {
      Object.assign(proxy, this.masterSunAngles(sun));
    });
    const update = (): void => {
      if (sun === 'primary') {
        this.opts.config.master.sunElevationDeg = proxy.elevation;
        this.opts.config.master.sunAzimuthDeg = proxy.azimuth;
        this.opts.config.sky.binary.centerElevation = proxy.elevation;
        this.opts.config.sky.binary.centerAzimuth = proxy.azimuth;
        this.opts.config.sky.binary.distance = proxy.distance;
        anglesToDirection(proxy, this.opts.config.sky.sunDir);
      } else {
        this.opts.config.master.secondarySun.relativeElevationDeg =
          proxy.elevation - this.opts.config.master.sunElevationDeg;
        this.opts.config.master.secondarySun.relativeAzimuthDeg =
          proxy.azimuth - this.opts.config.master.sunAzimuthDeg;
        this.opts.config.sky.binary.distance = proxy.distance;
        anglesToDirection(proxy, this.opts.config.sky.secondarySunDir);
      }
      this.commitSunPlacement();
    };
    folder.add(proxy, 'elevation', -45, 85, 0.1).name(`${label} elev`).onChange(update);
    folder.add(proxy, 'azimuth', -180, 180, 0.1).name(`${label} azim`).onChange(update);
    folder.add(proxy, 'distance', 20, 220, 1).name(`${label} dist`).onChange(update);
  }

  private masterSunAngles(sun: 'primary' | 'secondary'): {
    elevation: number;
    azimuth: number;
    distance: number;
  } {
    const distance = this.opts.config.sky.binary.distance
      || directionToAngles(this.opts.config.sky.sunDir).distance;
    if (sun === 'primary') {
      return {
        elevation: this.opts.config.master.sunElevationDeg,
        azimuth: this.opts.config.master.sunAzimuthDeg,
        distance,
      };
    }
    return {
      elevation: this.opts.config.master.sunElevationDeg
        + this.opts.config.master.secondarySun.relativeElevationDeg,
      azimuth: this.opts.config.master.sunAzimuthDeg
        + this.opts.config.master.secondarySun.relativeAzimuthDeg,
      distance,
    };
  }

  private syncMasterFromLegacySunDirs(): void {
    const primary = directionToAngles(this.opts.config.sky.sunDir);
    const secondary = directionToAngles(this.opts.config.sky.secondarySunDir);
    this.opts.config.master.sunElevationDeg = primary.elevation;
    this.opts.config.master.sunAzimuthDeg = primary.azimuth;
    this.opts.config.master.secondarySun.relativeElevationDeg =
      secondary.elevation - primary.elevation;
    this.opts.config.master.secondarySun.relativeAzimuthDeg =
      secondary.azimuth - primary.azimuth;
  }

  private syncDirectionControls(): void {
    for (const sync of this.directionSyncers) sync();
  }

  private clearMasterSunDirectionOverrides(): void {
    for (const path of MASTER_SUN_DIRECTION_PATHS) clearOverride(this.opts.config, path);
  }

  private commitSunPlacement(): void {
    this.clearMasterSunDirectionOverrides();
    this.syncDirectionControls();
    this.commit();
    this.refreshControllers();
  }

  private applyPreset(name: LightingPresetName): void {
    applyLightingPreset(this.opts.config, name);
    this.clearMasterSunDirectionOverrides();
    this.syncDirectionControls();
    this.refreshControllers();
    this.commit();
  }

  private addNullableString(
    folder: GUI,
    label: string,
    value: string | null,
    assign: (value: string | null) => void,
  ): void {
    const proxy = { value: value ?? '' };
    const commitString = (next: string): void => {
      const trimmed = String(next ?? '').trim();
      assign(trimmed.length > 0 ? trimmed : null);
      this.commit();
    };
    folder.add(proxy, 'value').name(label).onChange(commitString).onFinishChange(commitString);
  }

  private refreshControllers(): void {
    this.syncOverrideToggleProxies();
    for (const gui of this.guis.values()) {
      gui.controllersRecursive().forEach((c) => c.updateDisplay());
    }
  }
}

function readFolderState(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(FOLDER_STATE_KEY);
    return raw ? JSON.parse(raw) as Record<string, boolean> : {};
  } catch {
    return {};
  }
}

function writeFolderState(state: Record<string, boolean>): void {
  try {
    window.localStorage.setItem(FOLDER_STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function readActiveTab(fallback: TabKey): TabKey {
  try {
    const raw = window.localStorage.getItem(ACTIVE_TAB_KEY);
    if (raw === 'look') return 'sfx';
    if (
      raw === 'actions'
      || raw === 'lighting'
      || raw === 'sky'
      || raw === 'sfx'
      || raw === 'shadows'
      || raw === 'track'
      || raw === 'day'
    ) {
      return raw;
    }
  } catch { /* ignore */ }
  return fallback;
}

function writeActiveTab(tab: TabKey): void {
  try {
    window.localStorage.setItem(ACTIVE_TAB_KEY, tab);
  } catch { /* ignore */ }
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected || typeof document === 'undefined') return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.id = 'style-panel-tabs-styles';
  style.textContent = `
    .style-panel-tabbed {
      display: grid;
      grid-template-columns: ${TAB_BAR_WIDTH}px minmax(0, 1fr);
      grid-template-rows: auto minmax(0, 1fr);
      width: 100%;
      height: 100%;
      min-height: 0;
      overflow: hidden;
    }
    .style-panel-tabs {
      grid-column: 1;
      grid-row: 1 / span 2;
      padding: 6px 4px;
      background: rgba(8, 10, 16, 0.65);
      border-right: 1px solid rgba(255, 255, 255, 0.08);
      box-sizing: border-box;
    }
    .style-panel-savebar {
      grid-column: 2;
      grid-row: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 8px 10px;
      background: rgba(10, 12, 18, 0.78);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      box-sizing: border-box;
    }
    .style-panel-template {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: rgba(230, 236, 246, 0.9);
      font: 600 12px/1.2 ui-sans-serif, system-ui, sans-serif;
    }
    .style-panel-savebar button {
      appearance: none;
      border: 1px solid rgba(120, 160, 220, 0.55);
      background: rgba(120, 160, 220, 0.18);
      color: #fff;
      font: 700 11px/1 ui-sans-serif, system-ui, sans-serif;
      text-transform: uppercase;
      padding: 7px 9px;
      border-radius: 4px;
      cursor: pointer;
      white-space: nowrap;
    }
    .style-panel-savebar button:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
    .style-panel-tab {
      width: 42px;
      min-width: 42px;
      height: 34px;
      border-color: transparent;
      background: transparent;
      color: rgba(220, 226, 240, 0.72);
    }
    .style-panel-tab:hover {
      background: rgba(255, 255, 255, 0.06);
      color: #fff;
    }
    .style-panel-tab.is-active {
      background: rgba(120, 160, 220, 0.18);
      border-color: rgba(120, 160, 220, 0.55);
      color: #fff;
    }
    .style-panel-body {
      grid-column: 2;
      grid-row: 2;
      min-width: 0;
      overflow-y: auto;
      overflow-x: hidden;
    }
    .style-panel-tab-content {
      width: 100%;
    }
  `;
  document.head.appendChild(style);
}
